'use client'

import { format } from 'date-fns'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import type { DateRange } from 'react-day-picker'

import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { fieldVariants } from '@/components/ui/field-variants'
import { SegmentedControl } from '@/components/ui/segmented-control'
import { cn } from '@/lib/utils'

type GoToMode = 'date' | 'range'

// Denser than the default calendar (2rem cells, its own p-3): the panel supplies the padding.
const CALENDAR_CLASS = 'p-0 [--cell-size:1.875rem]'

const pad2 = (n: number) => String(n).padStart(2, '0')
const toTime = (date: Date) => `${pad2(date.getHours())}:${pad2(date.getMinutes())}`
function withTime(day: Date, time: string): Date {
  const [h, m] = time.split(':').map(Number)
  const next = new Date(day)
  next.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0)
  return next
}
function startOfToday() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  return today
}

/** One "day · time" row: the day reads off the calendar below, the time is typed. */
function DateTimeRow({
  day,
  time,
  onTimeChange,
  timeLabel,
}: {
  day: Date | undefined
  time: string
  onTimeChange: (time: string) => void
  timeLabel: string
}) {
  return (
    // gap-1 like the calendar caption's month/year dropdowns, so the panel's pairs share one
    // spacing. Two equal columns: neither field stretches into the other's room (a stretched time
    // field left its clock far from the text), and both line up over the calendar. No
    // calendar glyph on the day — the calendar sits right below.
    <div className="grid grid-cols-2 gap-1">
      <div className={cn(fieldVariants({ size: 'xs' }), 'min-w-0 cursor-default')}>
        <span className={cn('tabular-nums', !day && 'text-foreground/40')}>
          {day ? format(day, 'yyyy-MM-dd') : '—'}
        </span>
      </div>
      <input
        type="time"
        aria-label={timeLabel}
        value={time}
        onChange={(event) => onTimeChange(event.target.value)}
        className={cn(fieldVariants({ size: 'xs' }), 'min-w-0')}
      />
    </div>
  )
}

/**
 * Both charts' "Go to" — laid out like TradingView's own Go to dialog (Date / Custom range, a
 * day + time per bound, a calendar, Cancel / Go to). Percept has none, and TradingView's own
 * throws inside its gotoTime (`_density`: "Value is undefined") for a date before the loaded
 * history, so it never moved — each chart frames the result with its own setVisibleRange.
 */
export function ChartGoToPanel({
  onGoToDate,
  onGoToRange,
  onCancel,
}: {
  onGoToDate: (at: Date) => void
  onGoToRange: (from: Date, to: Date) => void
  onCancel: () => void
}) {
  const t = useTranslations('trade.chartToolbar')
  const tCommon = useTranslations('common')
  const [mode, setMode] = useState<GoToMode>('date')
  const [day, setDay] = useState<Date>(startOfToday)
  const [time, setTime] = useState('00:00')
  const [range, setRange] = useState<DateRange | undefined>(() => {
    const to = startOfToday()
    const from = new Date(to)
    from.setDate(from.getDate() - 7)
    return { from, to }
  })
  const [fromTime, setFromTime] = useState('00:00')
  const [toTime_, setToTime] = useState(() => toTime(new Date()))
  const today = new Date()

  const canGo = mode === 'date' || Boolean(range?.from && range?.to)
  const go = () => {
    if (mode === 'date') {
      onGoToDate(withTime(day, time))
      return
    }
    if (!range?.from || !range.to) return
    const from = withTime(range.from, fromTime)
    const to = withTime(range.to, toTime_)
    if (to.getTime() <= from.getTime()) return
    onGoToRange(from, to)
  }

  return (
    // As wide as the calendar (7 × 30px cells) — the rows stretch to it, nothing floats in
    // spare width.
    <div className="flex w-fit flex-col gap-2.5 p-3">
      <span className="text-foreground text-xs font-medium">{t('goTo')}</span>
      <SegmentedControl
        size="xs"
        fill
        value={mode}
        onChange={setMode}
        options={[
          { value: 'date', label: t('goToModeDate') },
          { value: 'range', label: t('goToModeRange') },
        ]}
      />
      {mode === 'date' ? (
        <DateTimeRow day={day} time={time} onTimeChange={setTime} timeLabel={tCommon('time')} />
      ) : (
        <div className="flex flex-col gap-1">
          <DateTimeRow
            day={range?.from}
            time={fromTime}
            onTimeChange={setFromTime}
            timeLabel={tCommon('time')}
          />
          <DateTimeRow
            day={range?.to}
            time={toTime_}
            onTimeChange={setToTime}
            timeLabel={tCommon('time')}
          />
        </div>
      )}
      {mode === 'date' ? (
        <Calendar
          mode="single"
          captionLayout="dropdown"
          selected={day}
          defaultMonth={day}
          endMonth={today}
          disabled={{ after: today }}
          onSelect={(next) => next && setDay(next)}
          showOutsideDays={false}
          className={CALENDAR_CLASS}
        />
      ) : (
        <Calendar
          mode="range"
          captionLayout="dropdown"
          selected={range}
          defaultMonth={range?.to ?? today}
          endMonth={today}
          disabled={{ after: today }}
          onSelect={setRange}
          showOutsideDays={false}
          className={CALENDAR_CLASS}
        />
      )}
      {/* The two actions stay clearly apart (unlike the field pairs above): a hairline Cancel
          against the filled Go to — the same fill family read as one button. */}
      <div className="grid grid-cols-2 gap-2">
        <Button type="button" variant="outline" size="xs" onClick={onCancel}>
          {tCommon('cancel')}
        </Button>
        <Button type="button" variant="cta" size="xs" disabled={!canGo} onClick={go}>
          {t('goTo')}
        </Button>
      </div>
    </div>
  )
}
